/**
 * Single source of truth for public, indexable routes.
 *
 * Anything listed here is automatically:
 *
 *   - emitted in `sitemap.xml` (vite-plugins/seo-files.ts)
 *   - treated as canonical-eligible (src/components/RouteCanonical.tsx)
 *   - given per-route SEO metadata (src/components/RouteSeo.tsx)
 *
 * To add a new public page (blog, docs, contact, etc.):
 *
 *   1. Append a new entry below.
 *   2. Add a matching `<Route>` in src/App.tsx.
 *   3. Add a SEO entry in src/components/RouteSeo.tsx (or rely on the
 *      site-wide default for now).
 *
 * This file is intentionally framework-free (no React imports) so the
 * Vite plugin that generates robots.txt/sitemap.xml can import it at
 * build time without pulling the entire client bundle.
 */

export type PublicRoute = {
  /** URL path, must start with `/`. Use `/` for the home page. */
  path: string;
  /**
   * Sitemap hint for how often the page changes. Search engines treat
   * this as advisory, not authoritative.
   */
  changefreq:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
  /**
   * Sitemap priority, 0.0–1.0. Relative weight WITHIN this site only;
   * does not influence cross-site ranking.
   */
  priority: string;
};

/** Editorial guide slugs — kept in sync with src/content/guides.ts. */
const GUIDE_SLUGS = [
  "ai-personal-trainer-guide",
  "how-to-use-an-ai-workout-generator",
  "ai-coach-vs-human-coach",
  "ai-meal-planner-guide",
  "ai-diet-plan-calories-and-macros",
  "ai-workout-planner-for-beginners",
  "online-coach-vs-in-person-trainer",
  "what-is-a-virtual-fitness-coach",
] as const;

/** Local SEO city slugs — kept in sync with src/content/locations.ts. */
const LOCATION_SLUGS = [
  "atlanta-ga",
  "marietta-ga",
  "sandy-springs-ga",
  "decatur-ga",
  "alpharetta-ga",
  "brisbane-qld",
  "gold-coast-qld",
  "logan-qld",
  "ipswich-qld",
] as const;

/**
 * Ordered roughly by importance — home > pricing > acquisition pages.
 * When you add new public pages (blog, docs, contact, case studies),
 * insert them here and they'll flow into the sitemap on the next build.
 */
export const PUBLIC_ROUTES: PublicRoute[] = [
  { path: "/", changefreq: "weekly", priority: "1.0" },
  { path: "/pricing", changefreq: "weekly", priority: "0.9" },
  { path: "/how-it-works", changefreq: "monthly", priority: "0.9" },
  { path: "/auth", changefreq: "monthly", priority: "0.5" },
  { path: "/signup-client", changefreq: "monthly", priority: "0.5" },
  { path: "/about", changefreq: "monthly", priority: "0.6" },
  { path: "/personalized-workout-plans", changefreq: "monthly", priority: "0.7" },
  { path: "/ai-nutrition-plan", changefreq: "monthly", priority: "0.9" },
  { path: "/how-we-use-ai", changefreq: "monthly", priority: "0.8" },
  { path: "/ai-personal-trainer", changefreq: "monthly", priority: "0.9" },
  { path: "/ai-workout-generator", changefreq: "monthly", priority: "0.9" },
  { path: "/ai-posing-coach", changefreq: "monthly", priority: "0.9" },
  { path: "/athlete-portal", changefreq: "weekly", priority: "0.9" },
  { path: "/portal", changefreq: "weekly", priority: "0.6" },
  { path: "/portal?tab=messages", changefreq: "weekly", priority: "0.5" },
  { path: "/guides", changefreq: "weekly", priority: "0.7" },
  ...GUIDE_SLUGS.map((slug): PublicRoute => ({
    path: `/guides/${slug}`,
    changefreq: "monthly",
    priority: "0.7",
  })),
  { path: "/locations", changefreq: "weekly", priority: "0.7" },
  ...LOCATION_SLUGS.map((slug): PublicRoute => ({
    path: `/locations/${slug}`,
    changefreq: "monthly",
    priority: "0.6",
  })),
  { path: "/blog", changefreq: "weekly", priority: "0.7" },
  { path: "/terms", changefreq: "yearly", priority: "0.3" },
  { path: "/privacy", changefreq: "yearly", priority: "0.3" },
  // Add future public routes here, e.g.:
  // { path: "/docs", changefreq: "monthly", priority: "0.6" },
  // { path: "/contact", changefreq: "yearly", priority: "0.4" },
];

/**
 * Convenience set lookup for canonical/SEO logic at runtime.
 * Cheap to construct; we materialise it once at module load.
 */
export const PUBLIC_ROUTE_PATHS: ReadonlySet<string> = new Set(
  PUBLIC_ROUTES.map((r) => r.path),
);
